/* eslint-disable import/no-default-export */
import { createTheme } from '@shopify/restyle';
import { PixelRatio, Platform } from 'react-native';

const palette = {
  greenBrand: '#00D9A5',
  greenSecondary: '#18A87D',
  greenSecondaryMild: '#DCF2EC',
  greenDark: '#035B60',
  yellow: '#E65E43',
  yellowBright: '#FBE676',
  yellowVivid: '#FBBD42',
  yellowVividMild: '#FDF4E0',
  alertRed: '#FF4444',
  alertRedMild: '#FFDADA',
  textPrimary: '#0A1629',
  textSecondary: '#303742',
  pastelBlue: '#1572A1',
  pastelPurple: '#655D8A',
  pastelMagenta: '#A85796',
  pastelOrange: '#CC7351',
  pastelYellow: '#E6B566',
  white: '#FFFFFF',
  grey01: '#F7F8FA',
  grey02: '#E8E8E8',
  grey03: '#BDBDBD',
  grey04: '#828282',
  grey05: '#303742',
  grey: ' #3C3C435C',
  black: '#000000',
  blue: '#138EFF',
  blueAlert: '#007AFF',
  blueMild: '#DCEEFF',
  purple: '#7B61FF',
  purpleMild: '#EBE7FF',
  magentaLight: '#C072CD',
  magentaLightMild: '#F6EAF8',
  magentaDark: '#A620B2',
  orange: '#FC7F5B',
  orangeMild: '#FFECE6',
  lightPurple: '#D9D5E3',
  grey06: 'rgba(0,0,0,0.5)',
  transparent: 'transparent',
  transparentBlack: 'rgba(0,0,0,0.7)',
  transparentGreenDark: 'rgba(3,91,96,0.5)',
  locationMeta: '#91929E',
};

const theme = createTheme({
  colors: {
    background: palette.white,
    onBackground: palette.textPrimary,
    onBackgroundSecondary: palette.textSecondary,
    onBackgroundTertiary: palette.grey04,
    surface: palette.white,
    onSurface: palette.textPrimary,
    onSurfaceSecondary: palette.textSecondary,
    buttonBackground: palette.black,
    androidShadow: `rgba(0,0,0,0.2)`,
    buttonText: palette.white,
    linkColor: palette.blue,

    // Scrim ladder — alpha-on-white tokens used for overlays on dark
    // surfaces (e.g. ChatImageViewer's dark chrome).
    scrimLight06: 'rgba(255,255,255,0.06)',
    scrimLight08: 'rgba(255,255,255,0.08)',
    scrimLight10: 'rgba(255,255,255,0.10)',
    scrimLight15: 'rgba(255,255,255,0.15)',
    scrimLight25: 'rgba(255,255,255,0.25)',
    scrimLight30: 'rgba(255,255,255,0.30)',
    scrimLight50: 'rgba(255,255,255,0.50)',

    // Scrim ladder — alpha-on-black tokens used for overlays on light
    // surfaces (dropdown hovers, dividers, disabled states).
    scrimDark04: 'rgba(0,0,0,0.04)',
    scrimDark08: 'rgba(0,0,0,0.08)',
    scrimDark10: 'rgba(0,0,0,0.10)',
    scrimDark20: 'rgba(0,0,0,0.20)',
    scrimDark25: 'rgba(0,0,0,0.25)',
    scrimDark30: 'rgba(0,0,0,0.30)',
    scrimDark35: 'rgba(0,0,0,0.35)',
    scrimDark50: palette.grey06, // same value as palette.grey06 — single source of truth

    // Bespoke navy surfaces for the viewer's floating selection-bar buttons.
    // Not part of the scrim ladder — kept named.
    viewerFloatingBtnBg: 'rgba(28,34,48,0.92)',
    viewerFloatingBtnBgHover: 'rgba(50,62,86,0.95)',
    // Solid chrome surfaces for the viewer's header, thumbnail bar, and pills.
    viewerChrome: '#1D222F',
    // Deep-black canvas behind the previewed image.
    viewerImageBg: '#0D1117',

    ...palette,
  },
  spacing: {
    none: 0,
    xxxs: 2,
    xxs: 4,
    xs: 8,
    s: 12,
    m: 16,
    l: 24,
    xl: 36,
    xxl: 48,
    listFooter: 80,
  } as const,
  borderRadii: {
    none: 0,
    xxxs: 2,
    xxs: 4,
    xs: 8,
    s: 12,
    m: 16,
    l: 24,
    xl: 32,
    xxl: 48,
  } as const,
  breakpoints: {
    phone: 0,
    tablet: 768,
  },
  textVariants: {
    defaults: {
      // same as body
      fontFamily: 'Inter_400Regular',
      fontSize: {
        phone: 16,
      },
      lineHeight: 24,
      color: 'onSurface',
    },
    heading1: {
      fontFamily: 'Inter_600SemiBold',
      fontSize: {
        phone: 28,
        tablet: 32,
      },
      lineHeight: {
        phone: 32,
        tablet: 36,
      },
      color: 'onSurface',
    },
    heading1Web: {
      fontFamily: 'Inter_600SemiBold',
      fontSize: 28,
      lineHeight: 32,
    },
    heading2: {
      fontFamily: 'Inter_600SemiBold',
      fontSize: 22,
      lineHeight: 32,
      color: 'onSurface',
    },
    heading3: {
      fontFamily: 'Inter_600SemiBold',
      fontSize: 32,
      lineHeight: 36,
      color: 'textPrimary',
    },
    labelLarge: {
      fontFamily: 'Inter_500Medium',
      fontSize: 18,
      lineHeight: 24,
      color: 'onSurface',
    },
    labelLargeWeb: {
      fontFamily: 'Inter_500Medium',
      fontSize: 18,
      lineHeight: 24,
      color: 'onSurface',
      fontWeight: 700,
    },
    labelEmphasized: {
      fontFamily: 'Inter_600SemiBold',
      fontSize: 16,
      lineHeight: 24,
      color: 'onSurface',
    },
    sidebarNavLabel: {
      fontFamily: 'Inter_500Medium',
      fontWeight: '500',
      fontSize: 14,
      lineHeight: 16,
      color: 'textSecondary',
    },
    labelEmphasized14: {
      fontFamily: 'Inter_600SemiBold',
      fontSize: 14,
      lineHeight: 21,
      color: 'onSurface',
    },
    labelEmphasizedDisabled: {
      fontFamily: 'Inter_600SemiBold',
      fontSize: 16,
      lineHeight: 24,
      color: 'grey04',
    },
    labelEmphasizedChatDetialTitle: {
      fontFamily: 'Inter_600SemiBold',
      fontSize: 16,
      lineHeight: 21,
      color: 'textPrimary',
    },
    labelEmphasizedNudgeTitle: {
      fontFamily: 'Inter_600SemiBold',
      fontSize: 16,
      lineHeight: 21,
      color: 'white',
    },
    labelEmphasizedResponsive: {
      fontFamily: 'Inter_600SemiBold',
      fontSize: 12,
      lineHeight: 24,
      color: 'textSecondary',
    },
    labelDisabledStrikeThrough: {
      fontFamily: 'Inter_400Regular',
      fontSize: 14,
      lineHeight: 16,
      letterSpacing: 0.02,
      textAlign: 'left',
      textDecorationLine: 'line-through',
      color: 'grey04',
    },
    labelHeroCarousel: {
      fontFamily: 'Inter_400Regular',
      fontSize: 10.5,
      lineHeight: 12,
      letterSpacing: 0.02,
      textAlign: 'left',
    },
    bodyTaskName: {
      fontWeight: 500,
      fontFamily: 'Inter_500Medium',
      fontSize: 14,
      lineHeight: 16,
      color: 'textPrimary',
    },
    taskBtnLabel: {
      fontFamily: 'Inter_500Medium',
      fontSize: 14,
      lineHeight: 16,
      color: 'grey04',
      fontWeight: '500',
    },
    labelAssignedTask: {
      fontFamily: 'Inter_500Medium',
      fontSize: 14,
      lineHeight: 16,
      color: 'textSecondary',
    },
    labelCompletedTask: {
      fontFamily: 'Inter_500Medium',
      fontSize: 14,
      lineHeight: 16,
      color: 'greenDark',
    },
    buttonLabel: {
      fontFamily: 'Inter_500Medium',
      fontSize: 16,
      lineHeight: 24,
      color: 'buttonText',
    },
    searchLabel: {
      fontFamily: 'Inter_500Medium',
      fontSize: 16,
      lineHeight: 24,
    },
    actionLabelSemiBold: {
      fontFamily: 'Inter_600SemiBold',
      fontSize: 16,
      lineHeight: 24,
    },
    labelSmall: {
      fontFamily: 'Inter_500Medium',
      fontSize: 14,
      lineHeight: 18,
    },
    labelSmallBold: {
      fontFamily: 'Inter_600SemiBold',
      fontSize: 14,
      lineHeight: 18,
    },
    tabBarLabelActive: {
      fontFamily: 'Inter_500Medium',
      fontSize: 12,
      lineHeight: 16,
    },
    tabBarLabelInactive: {
      fontFamily: 'Inter_400Regular',
      fontSize: 12,
      lineHeight: 16,
    },
    labelSearch: {
      fontFamily: 'Inter_500Medium',
      fontSize: 16,
      lineHeight: 21,
      color: 'textPrimary',
    },
    labelRequired: {
      fontFamily: 'Inter_500Medium',
      fontSize: 10,
      lineHeight: 16,
    },
    headerLabel: {
      fontWeight: 600,
      fontFamily: 'Inter_500Medium',
      fontSize: 16,
      lineHeight: 21,
      color: 'textSecondary',
    },
    body: {
      fontFamily: 'Inter_400Regular',
      fontSize: 16,
      lineHeight: 24,
      color: 'onSurface',
    },
    body1: {
      fontFamily: 'Inter_400Regular',
      fontSize: 16,
      lineHeight: 21,
      color: 'onSurfaceSecondary',
    },
    link: {
      fontFamily: 'Inter_400Regular',
      fontSize: 14,
      lineHeight: 20,
      color: 'linkColor',
    },
    bodySecondary: {
      fontFamily: 'Inter_400Regular',
      fontSize: {
        phone: 14,
      },
      lineHeight: {
        phone: 20,
      },
      letterSpacing: 0.2,
      color: 'onSurface',
    },
    bodySecondaryNew: {
      fontFamily: 'Inter_400Regular',
      fontSize: 14,
      lineHeight: 16,
      letterSpacing: 0.2,
      color: 'textPrimary',
    },
    bodySecondaryBold: {
      fontFamily: 'Inter_500Medium',
      fontSize: 14,
      fontWeight: 700,
      lineHeight: 16,
      letterSpacing: 0.4,
      color: 'textPrimary',
    },
    bodySecondary1: {
      fontFamily: 'Inter_400Regular',
      fontSize: 14,
      lineHeight: 16,
      letterSpacing: 0.4,
      color: 'textPrimary',
    },
    bodySecondary2: {
      fontFamily: 'Inter_400Regular',
      fontSize: 8,
      lineHeight: 12,
      letterSpacing: 0.2,
      color: 'textSecondary',
    },
    webBody: {
      fontFamily: 'Inter_500Medium',
      fontSize: 14,
      fontWeight: '500',
      lineHeight: 16,
      letterSpacing: 0.2,
      color: 'textPrimary',
    },
    webSmall: {
      fontFamily: 'Inter_500Medium',
      fontSize: 14,
      fontWeight: '500',
      lineHeight: 16,
      color: 'textSecondary',
    },
    webBodySecondary: {
      fontFamily: 'Inter_400Regular',
      fontSize: 14,
      fontWeight: '400',
      lineHeight: 16,
      letterSpacing: 0.2,
      color: 'textPrimary',
    },
    webBodyHintSubline: {
      fontFamily: 'Inter_400Regular',
      fontSize: 12,
      fontWeight: '400',
      lineHeight: 16,
      letterSpacing: 0.2,
      color: 'grey04',
    },
    metadata: {
      fontFamily: 'Inter_400Regular',
      fontSize: 12,
      lineHeight: 16,
      color: 'onSurface',
    },
    metadataSemiBold: {
      fontFamily: 'Inter_600SemiBold',
      fontSize: 12,
      lineHeight: 16,
      color: 'onSurface',
    },
    metadata1: {
      fontFamily: 'Inter_400Regular',
      fontSize: 14,
      lineHeight: 16,
      color: 'textSecondary',
    },
    metadataSecondary: {
      fontFamily: 'Inter_400Regular',
      fontSize: 10,
      lineHeight: 16,
      color: 'onSurface',
    },
    metadataSecondaryNew: {
      fontFamily: 'Inter_500Medium',
      fontSize: 10,
      lineHeight: 12,
      color: 'onSurface',
    },
    metadataSmall: {
      fontFamily: 'Inter_400Regular',
      fontSize: 10,
      lineHeight: 12,
      color: 'textPrimary',
    },
    metadataSmaller: {
      fontFamily: 'Inter_400Regular',
      fontSize: 8,
      lineHeight: 12,
      color: 'grey04',
    },
    typography: {
      fontFamily: 'Inter_500Medium',
      fontWeight: '500',
      fontSize: 16,
      lineHeight: 24,
      color: 'white',
    },
    typoMetadata: {
      fontFamily: 'Inter_400Regular',
      fontWeight: '400',
      fontSize: 11,
      lineHeight: 15,
      color: 'surface',
    },
    error: {
      fontFamily: 'Inter_400Regular',
      fontSize: 12,
      lineHeight: 16,
      color: 'alertRed',
    },
    labelTabHeaderText: {
      letterSpacing: 0,
      fontFamily: 'Inter_500Medium',
      fontSize: 10,
      lineHeight: 16,
      color: 'grey04',
    },
    labelTableBodyText: {
      fontFamily: 'Inter_400Regular',
      fontSize: 14,
      lineHeight: 16,
      color: 'textPrimary',
    },
    labelDueDateText: {
      fontFamily: 'Inter_400Regular',
      fontSize: 14,
      lineHeight: 16,
      color: 'textPrimary',
      textAlign: 'center',
      //marginRight: 'm',
    },
    alertLabel: {
      fontFamily: 'Inter_500Medium',
      fontWeight: '600',
      fontSize: 17,
      lineHeight: 22,
    },
  },
  shadowBoxVariants: {
    defaults: {
      borderColor: 'grey02',
      borderStyle: 'solid',
      backgroundColor: 'white',
      elevation: 5,
      shadowColor:
        Platform.OS === 'android' ? 'androidShadow' : 'buttonBackground',
      shadowOpacity: 0.05,
      shadowOffset: { width: 0, height: 5 },
      shadowRadius: 25,
    },
    base: {
      elevation: 5,
      shadowColor:
        Platform.OS === 'android' ? 'androidShadow' : 'buttonBackground',
      shadowOpacity: 0.05,
      shadowOffset: { width: 0, height: 5 },
      shadowRadius: 25,
    },
    dark: {
      elevation: 5,
      shadowColor:
        Platform.OS === 'android' ? 'androidShadow' : 'buttonBackground',
      shadowOpacity: 0.1,
      shadowOffset: { width: 0, height: 5 },
      shadowRadius: 12,
    },
    promoCard: {
      backgroundColor: 'black',
    },
    thin: {
      elevation: 4,
      shadowColor:
        Platform.OS === 'android' ? 'androidShadow' : 'buttonBackground',
      shadowOpacity: 0.06,
      shadowOffset: { width: 0, height: 4 },
      shadowRadius: 6,
    },
    none: {
      shadowOpacity: 0,
    },
  },
  buttonVariants: {
    defaults: {
      flexDirection: 'row',
      alignItems: 'center',
      backgroundColor: 'grey02',
      padding: 's',
      borderRadius: 'xs',
    },
    primary: {
      backgroundColor: 'black',
      borderColor: 'black',
      borderWidth: 1,
    },
    cancel: {
      backgroundColor: 'white',
      borderColor: 'grey02',
      borderWidth: 1,
    },
    text: {
      backgroundColor: undefined,
      borderWidth: 0,
    },
    textweb: {
      backgroundColor: undefined,
      borderWidth: 0,
    },
    textSecondary: {
      backgroundColor: undefined,
      borderWidth: 0,
    },
    create: {
      borderWidth: 1,
      borderColor: 'grey03',
      backgroundColor: 'grey01',
    },
    copy: {
      borderWidth: 1,
      borderColor: 'grey03',
      backgroundColor: 'grey01',
    },
    pending: {
      borderWidth: 1,
      borderColor: 'grey03',
      backgroundColor: 'grey01',
    },
    edit: {
      borderWidth: 1,
      borderColor: 'grey03',
      backgroundColor: 'grey01',
    },
    editWhite: {
      borderWidth: 1,
      borderColor: 'grey03',
      backgroundColor: 'grey01',
    },
    editError: {
      borderWidth: 1,
      borderColor: 'alertRed',
      backgroundColor: 'grey01',
    },
    editSelected: {
      borderWidth: 1,
      borderColor: 'grey03',
      backgroundColor: 'grey01',
    },
    editSelectedDisabled: {
      borderWidth: 1,
      borderColor: 'grey03',
      backgroundColor: 'grey01',
    },
    editSelectedDisabledDark: {
      borderWidth: 1,
      borderColor: 'grey03',
      backgroundColor: 'grey02',
    },
    clearFilter: {
      backgroundColor: 'white',
      color: 'grey04',
    },
    editBold: {
      borderWidth: 1,
      borderColor: 'grey03',
      backgroundColor: 'grey01',
    },
    reversed: {
      borderWidth: 1,
      borderColor: 'black',
      backgroundColor: 'white',
    },
    icon: {
      borderWidth: 0,
      backgroundColor: 'black',
      borderRadius: 'l',
      padding: 's',
    },
    delete: {
      borderWidth: 0,
      backgroundColor: 'grey02',
    },
    logout: {
      borderWidth: 0,
      backgroundColor: 'white',
    },
    webprimary: {
      backgroundColor: 'black',
      borderColor: 'black',
      borderWidth: 1,
    },
    greenSecondary: {
      backgroundColor: 'greenSecondary',
      borderWidth: 0,
    },
    upload: {
      backgroundColor: 'white',
      borderWidth: 0,
    },
    textEmphasized: {
      backgroundColor: undefined,
      borderWidth: 0,
    },
    textGreenAction: {
      backgroundColor: undefined,
      borderWidth: 0,
    },
  },
  badgeVariants: {
    defaults: {
      flexDirection: 'row',
      alignItems: 'center',
      backgroundColor: 'grey02',
      padding: 'xs',
      borderRadius: 'xs',
    },
    red: {
      backgroundColor: 'alertRedMild',
    },
    yellow: {
      backgroundColor: 'yellowVividMild',
    },
    grey: {
      backgroundColor: 'grey02',
    },
    green: {
      backgroundColor: 'greenSecondary',
    },
    grey01: {
      backgroundColor: 'grey01',
    },
  },
  tagVariants: {
    defaults: {
      flexDirection: 'row',
      alignItems: 'center',
      backgroundColor: 'grey01',
      color: 'black',
      padding: 's',
      borderRadius: 'xs',
    },
    reversed: {
      backgroundColor: 'black',
      color: 'white',
      borderWidth: 0,
    },
    primary: {
      backgroundColor: 'blueMild',
      color: 'blue',
    },
    primaryReversed: {
      backgroundColor: 'blue',
      color: 'white',
    },
    secondary: {
      backgroundColor: 'greenSecondaryMild',
      color: 'greenSecondary',
    },
    filters: {
      backgroundColor: 'greenSecondaryMild',
      color: 'greenDark',
    },
    secondaryReversed: {
      backgroundColor: 'greenSecondary',
      color: 'white',
    },
    tertiary: {
      backgroundColor: 'magentaLightMild',
      color: 'magentaLight',
    },
    tertiaryReversed: {
      backgroundColor: 'magentaLight',
      color: 'white',
    },
    info: {
      backgroundColor: 'purpleMild',
      color: 'purple',
    },
    infoReversed: {
      backgroundColor: 'purple',
      color: 'white',
    },
    infoSecondary: {
      backgroundColor: 'orangeMild',
      color: 'orange',
    },
    infoSecondaryReversed: {
      backgroundColor: 'orange',
      color: 'white',
    },
    badge: {
      backgroundColor: 'white',
      color: 'black',
      borderWidth: 1,
      borderColor: 'grey02',
    },
    picker: {
      backgroundColor: 'grey01',
      color: 'grey05',
    },
    pickerForWeb: {
      backgroundColor: 'greenSecondaryMild',
      color: 'greenDark',
    },
    webFolder: {
      backgroundColor: 'transparent',
      color: 'grey04',
    },
    folder: {
      backgroundColor: 'grey02',
      color: 'grey05',
    },
    folderGreen: {
      backgroundColor: 'greenSecondaryMild',
      color: 'greenSecondary',
    },
    status: {
      backgroundColor: 'grey01',
      color: 'textPrimary',
    },
    textSecondary: {
      backgroundColor: 'grey01',
      color: 'textSecondary',
    },
    filter: {
      backgroundColor: 'grey01',
      color: 'greenSecondary',
    },
    assignedTask: {
      backgroundColor: 'magentaLightMild',
      color: 'pastelMagenta',
    },
    completedTask: {
      backgroundColor: 'greenSecondaryMild',
      color: 'greenDark',
    },
    joinTask: {
      backgroundColor: 'magentaLightMild',
      color: 'magentaDark',
    },
    checkIn: {
      backgroundColor: 'greenSecondaryMild',
      color: 'greenSecondary',
    },
    checkInError: {
      backgroundColor: 'alertRedMild',
      color: 'alertRed',
    },
  },
});

const MAX_FONT_SCALE = 1.3;

export const getFontScale = () => {
  const scale = PixelRatio.getFontScale();
  return Math.min(scale, MAX_FONT_SCALE);
};

export const getScaledSize = (baseSize: number) => {
  return baseSize * getFontScale();
};

export const getScaledSpacing = (spacingKey: keyof typeof theme.spacing) => {
  return theme.spacing[spacingKey] * getFontScale();
};

export const SCALED_DIMENSIONS = {
  BUTTON_HEIGHT: 32,
  // Floating action button ("+ New Task", "+ New Project", "+ New Update").
  // Not a spacing token doing size duty — see FloatingActionButton (TA-10614).
  FAB_HEIGHT: 48,
  ICON_SIZE: 16,
  DAY_CELL: 40,
  DAY_CELL_RADIUS: 20,
  AVATAR_SMALL: 32,
  AVATAR_MEDIUM: 40,
  AVATAR_LARGE: 56,
  INPUT_HEIGHT: 40,
  CALENDAR_HEADER: 32,
  CALENDAR_FOOTER: 64,
  MARGIN_STANDARD: 18,
  // Dot badge shown on filter buttons when at least one filter is active.
  FILTER_DOT: 10,
} as const;

export const getCommonScaledDimensions = () => ({
  buttonHeight: getScaledSize(SCALED_DIMENSIONS.BUTTON_HEIGHT),
  iconSize: getScaledSize(SCALED_DIMENSIONS.ICON_SIZE),
  inputHeight: getScaledSize(SCALED_DIMENSIONS.INPUT_HEIGHT),
  calendarHeader: getScaledSize(SCALED_DIMENSIONS.CALENDAR_HEADER),
  calendarFooter: getScaledSize(SCALED_DIMENSIONS.CALENDAR_FOOTER),
  marginStandard: getScaledSize(SCALED_DIMENSIONS.MARGIN_STANDARD),
  dayCell: {
    width: getScaledSize(SCALED_DIMENSIONS.DAY_CELL),
    height: getScaledSize(SCALED_DIMENSIONS.DAY_CELL),
    borderRadius: getScaledSize(SCALED_DIMENSIONS.DAY_CELL_RADIUS),
  },
  dayCellDimensions: {
    scaledHeight: getScaledSize(SCALED_DIMENSIONS.DAY_CELL),
    scaledDaySize: getScaledSize(SCALED_DIMENSIONS.DAY_CELL),
    borderRadius: getScaledSize(SCALED_DIMENSIONS.DAY_CELL_RADIUS),
  },
});

// TODO: Add the web text styles

export type Theme = typeof theme;
export default theme;
